import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ScrollThread } from "@/components/ScrollThread";
import Home from "@/pages/Home";
import Research from "@/pages/Research";
import ResearchDetail from "@/pages/ResearchDetail";
import ResearchNote01 from "@/pages/ResearchNote01";
import ResearchNote02 from "@/pages/ResearchNote02";
import ResearchDialogues from "@/pages/ResearchDialogues";
import SessionPage from "@/pages/SessionPage";
import Roundtables from "@/pages/Roundtables";
import ReadingCircle from "@/pages/ReadingCircle";
import Advisory from "@/pages/Advisory";
import Community from "@/pages/Community";
import About from "@/pages/About";
import AdminLogin from "@/pages/admin/Login";
import AdminDashboard from "@/pages/admin/Dashboard";

function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const t = setTimeout(() => {
        document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
      }, 400);
      return () => clearTimeout(t);
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function Shell() {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <div className="grain min-h-screen bg-bone text-ink">
      <ScrollManager />
      {!isAdmin && <ScrollThread />}
      {!isAdmin && <Navbar />}
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/research" element={<Research />} />
        <Route path="/research/:id" element={<ResearchDetail />} />
        <Route path="/research-note-01" element={<ResearchNote01 />} />
        <Route path="/research-note-02" element={<ResearchNote02 />} />
        <Route path="/research-dialogues" element={<ResearchDialogues />} />
        <Route path="/session/:slug" element={<SessionPage />} />
        <Route path="/roundtables" element={<Roundtables />} />
        <Route path="/reading-circle" element={<ReadingCircle />} />
        <Route path="/practice" element={<Advisory />} />
        <Route path="/advisory" element={<Navigate to="/practice" replace />} />
        {/* Legacy URL migrations (pre-redesign site) */}
        <Route path="/capabilities" element={<Navigate to="/practice" replace />} />
        <Route path="/contact" element={<Navigate to="/about" replace />} />
        <Route path="/publications" element={<Navigate to="/research" replace />} />
        <Route path="/session1" element={<Navigate to="/session/session1" replace />} />
        <Route path="/session2" element={<Navigate to="/session/session2" replace />} />
        <Route path="/session3" element={<Navigate to="/session/session3" replace />} />
        <Route path="/session4" element={<Navigate to="/session/session4" replace />} />
        <Route path="/community" element={<Community />} />
        <Route path="/about" element={<About />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminDashboard />} />
      </Routes>
      {!isAdmin && <Footer />}
      <Toaster position="bottom-right" />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Shell />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
